import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API_KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const initialPagination = { current_page: 1, last_page: 1, per_page: 20, total: 0 };
const isUnseen = (status) => [true, 1, "1", "unseen", "new", "unread"].includes(status);
const safeText = (value) => value == null ? "" : String(value);

// Notification HTML comes from the API. Render as HTML only after sanitizing it.
// DOMPurify is used if it is already available in the host app; otherwise show plain text.
const NotificationContent = ({ html }) => {
  const raw = safeText(html);
  if (typeof window !== "undefined" && window.DOMPurify?.sanitize) {
    return <div className="rmn-html" dangerouslySetInnerHTML={{ __html: window.DOMPurify.sanitize(raw) }} />;
  }
  return <div className="rmn-html rmn-plain">{raw.replace(/<[^>]*>/g, " ") || "No notification details available."}</div>;
};

const BasicDatatable = () => {
  const [income, setIncome] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const listRequest = useRef(0);
  const detailRequest = useRef(0);

  const fetchIncome = useCallback(async (page = 1) => {
    const token = localStorage.getItem("auth_token");
    if (!token) { setIncome([]); setPagination(initialPagination); setError("Please sign in to view notifications."); return; }
    const requestId = ++listRequest.current;
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`/apps-notifications?page=${page}`,
        { key_s: API_KEY, currency: localStorage.getItem("selectedCurrency") },
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } });
      if (requestId !== listRequest.current) return;
      if (response.data.status === "success") {
        setIncome(Array.isArray(response.data.data) ? response.data.data : []);
        setPagination({
          current_page: Number(response.data.pagination?.current_page) || page,
          last_page: Number(response.data.pagination?.last_page) || 1,
          per_page: Number(response.data.pagination?.per_page) || 20,
          total: Number(response.data.pagination?.total) || 0,
        });
      } else { setError(response.data.message || "Unable to retrieve notifications."); }
    } catch (e) {
      if (requestId === listRequest.current) setError("Couldn't refresh notifications. Please try again.");
    } finally { if (requestId === listRequest.current) setLoading(false); }
  }, []);

  useEffect(() => { fetchIncome(1); return () => { listRequest.current += 1; detailRequest.current += 1; }; }, [fetchIncome]);

  const closeModal = () => {
    detailRequest.current += 1;
    setShowModal(false);
    setSelectedNotification(null);
    setDetailsLoading(false);
    setDetailsError("");
  };

  const openDetails = async (id) => {
    const requestId = ++detailRequest.current;
    const token = localStorage.getItem("auth_token");
    setSelectedNotification(null);
    setDetailsError("");
    setDetailsLoading(true);
    setShowModal(true);
    try {
      const response = await axios.post(`/apps-notification-details/${id}`,
        { key_s: API_KEY, currency: localStorage.getItem("selectedCurrency") },
        { headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      if (requestId !== detailRequest.current) return;
      if (response.data.status === "success") {
        setSelectedNotification(response.data.data);
        // The backend may mark this notification as seen when details are fetched.
        fetchIncome(pagination.current_page);
      } else { setDetailsError(response.data.message || "Notification details are unavailable."); }
    } catch (e) { if (requestId === detailRequest.current) setDetailsError("Unable to open this notification."); }
    finally { if (requestId === detailRequest.current) setDetailsLoading(false); }
  };

  useEffect(() => {
    if (!showModal) return;
    const onKeyDown = (e) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showModal]);

  const currentPage = pagination.current_page;
  const totalPages = Math.max(1, pagination.last_page);
  const first = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, i) => first + i);
  const filtered = income.filter(row => {
    if (filter === "unread" && !isUnseen(row.status)) return false;
    if (filter === "read" && isUnseen(row.status)) return false;
    return `${safeText(row.title)} ${safeText(row.date)}`.toLowerCase().includes(search.toLowerCase());
  });
  const unreadCount = income.filter(row => isUnseen(row.status)).length;

  return <>
    <style>{`
      body{background:#00191D!important;color:#fff!important}
      .rmn-root,.rmn-root *{box-sizing:border-box}
      .rmn-root{--navy:#00191D;--cyan:#16B8C7;--line:rgba(22,184,199,.22);--muted:#9eb9bf;min-height:100vh;background:#00191D;color:#fff;font:400 14px/1.5 inherit;padding:18px 4px 42px}
      .rmn-wrap{width:100%;max-width:1220px;margin:0 auto}
      .rmn-hero{border:1px solid var(--line);border-radius:20px;padding:23px;background:linear-gradient(115deg,#06343c,#00191D 68%);display:flex;align-items:center;justify-content:space-between;gap:15px;animation:rmn-enter .3s ease-out}
      .rmn-heading{display:flex;align-items:center;gap:13px;min-width:0}
      .rmn-logo{width:48px;height:48px;flex:0 0 48px;border-radius:15px;background:linear-gradient(135deg,#3ce0e5,#16B8C7);display:grid;place-items:center;color:#002a30;font-size:20px}
      .rmn-hero h2{margin:0;color:white;font-size:22px;font-weight:800}
      .rmn-hero p{margin:4px 0 0;color:#b6d2d7;font-size:13px}
      .rmn-total{background:rgba(22,184,199,.12);border:1px solid var(--line);border-radius:12px;padding:9px 14px;text-align:center;white-space:nowrap}
      .rmn-total strong{font-size:18px;color:#56e4e8;display:block;line-height:1.2}
      .rmn-total span{font-size:11px;color:#b6d2d7}
      .rmn-panel{margin-top:14px;background:#06242a;border:1px solid var(--line);border-radius:20px;padding:18px;animation:rmn-enter .35s ease-out}
      .rmn-tools{display:flex;gap:10px;align-items:center;justify-content:space-between;flex-wrap:wrap;margin-bottom:16px}
      .rmn-tabs{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
      .rmn-tab,.rmn-btn,.rmn-page,.rmn-close{cursor:pointer;transition:background .2s,border-color .2s,transform .2s}
      .rmn-tab{border:1px solid var(--line);background:#092f36;color:#d0e7ea;border-radius:10px;padding:9px 13px;font-weight:650}
      .rmn-tab.active{background:#e8fbfc;color:#06313a;border-color:#e8fbfc}
      .rmn-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
      .rmn-search{min-width:180px;width:min(260px,100%);height:39px;background:#00191D;border:1px solid var(--line);border-radius:10px;padding:0 12px;color:#fff;outline:0}
      .rmn-search:focus{border-color:#16B8C7}
      .rmn-search::placeholder{color:#8eafb6}
      .rmn-btn{border:0;border-radius:10px;background:linear-gradient(110deg,#16B8C7,#53dce0);color:#00313a;padding:10px 15px;font-weight:800;white-space:nowrap}
      .rmn-btn:hover:not(:disabled){transform:translateY(-1px)}
      .rmn-btn:disabled,.rmn-page:disabled{opacity:.45;cursor:not-allowed}
      .rmn-list{display:grid;gap:10px}
      .rmn-item{width:100%;text-align:left;display:flex;align-items:center;gap:13px;background:#092e35;border:1px solid rgba(255,255,255,.1);border-radius:15px;padding:15px;color:#fff;cursor:pointer;transition:transform .18s,border-color .18s,background .18s}
      .rmn-item:hover{transform:translateY(-2px);border-color:rgba(22,184,199,.7);background:#0a3941}
      .rmn-item.unread{border-left:3px solid #16B8C7}
      .rmn-item-icon{height:43px;width:43px;flex:0 0 43px;border-radius:13px;background:#e7fafa;color:#008f9e;display:grid;place-items:center;font-size:17px}
      .rmn-item-body{flex:1;min-width:0}
      .rmn-item-title{font-size:14px;font-weight:750;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0}
      .rmn-date{font-size:12px;color:var(--muted);margin-top:4px}
      .rmn-status{border-radius:20px;padding:5px 10px;font-size:11px;font-weight:750;background:rgba(255,255,255,.09);color:#b6cdd2}
      .rmn-status.new{background:rgba(22,184,199,.17);color:#65e5e8}
      .rmn-empty{padding:50px 15px;text-align:center;color:#a8c4ca;background:#092d34;border:1px dashed var(--line);border-radius:15px}
      .rmn-empty i{font-size:27px;color:#16B8C7;display:block;margin-bottom:10px}
      .rmn-error{padding:10px 13px;background:#3d1f2a;border:1px solid #854957;color:#ffd6dd;border-radius:11px;margin-bottom:12px}
      .rmn-footer{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-top:18px;border-top:1px solid var(--line);padding-top:16px}
      .rmn-footnote{font-size:12px;color:var(--muted)}
      .rmn-pages{display:flex;gap:6px;flex-wrap:wrap}
      .rmn-page{min-width:35px;height:35px;padding:0 9px;border-radius:9px;background:#00191D;border:1px solid var(--line);color:white;font-weight:700}
      .rmn-page.active{background:linear-gradient(110deg,#16B8C7,#54e0e2);color:#002b32;border-color:transparent}
      .rmn-page:hover:not(:disabled){border-color:#16B8C7}
      .rmn-overlay{position:fixed;inset:0;z-index:999999;background:rgba(0,14,17,.83);display:flex;align-items:center;justify-content:center;padding:12px;animation:rmn-enter .2s ease-out}
      .rmn-modal{background:#06272d;border:1px solid rgba(22,184,199,.38);color:white;border-radius:20px;width:100%;max-width:660px;max-height:calc(100dvh - 24px);display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.4)}
      .rmn-modal-head{padding:16px 18px;border-bottom:1px solid var(--line);display:flex;align-items:center;justify-content:space-between;gap:10px;background:#08343c}
      .rmn-modal-head h3{margin:0;font-size:16px;color:#fff;font-weight:800}
      .rmn-close{background:#fff;color:#00313a;border:0;border-radius:9px;height:34px;width:34px;font-size:22px;line-height:1}
      .rmn-modal-body{padding:20px;overflow-y:auto;min-height:100px}
      .rmn-modal-title{font-size:20px;color:#fff;margin:0 0 8px;font-weight:800}
      .rmn-modal-date{color:#a6c5ca;font-size:12px;margin-bottom:17px}
      .rmn-html{border:1px solid var(--line);border-radius:12px;padding:16px;background:#00191D;color:#e6f4f5;line-height:1.75;overflow-wrap:anywhere;font-size:14px;white-space:normal}
      .rmn-html.rmn-plain{white-space:pre-wrap}
      .rmn-html img{max-width:100%;height:auto}.rmn-html a{color:#55e4e6}.rmn-html table{max-width:100%;display:block;overflow:auto}
      @keyframes rmn-enter{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      @media(max-width:650px){.rmn-root{padding:10px 4px 24px}.rmn-hero{padding:15px;border-radius:15px}.rmn-hero h2{font-size:17px}.rmn-hero p{font-size:11px}.rmn-logo{height:40px;width:40px;flex-basis:40px}.rmn-total{padding:7px 9px}.rmn-panel{padding:10px;border-radius:15px}.rmn-tools,.rmn-actions{align-items:stretch}.rmn-actions{width:100%}.rmn-search{flex:1;min-width:120px}.rmn-item{padding:11px;gap:9px}.rmn-item-icon{height:37px;width:37px;flex-basis:37px}.rmn-status{padding:4px 7px;font-size:10px}.rmn-footer{justify-content:center;text-align:center}.rmn-modal{border-radius:15px}.rmn-modal-body{padding:13px}}
      @media(prefers-reduced-motion:reduce){.rmn-root *{animation:none!important;transition:none!important}}
    `}</style>
    <main className="rmn-root"><div className="rmn-wrap">
      <header className="rmn-hero">
        <div className="rmn-heading"><div className="rmn-logo"><i className="fas fa-bell" /></div><div><h2>Notifications</h2><p>Stay informed about your account activity</p></div></div>
        <div className="rmn-total"><strong>{pagination.total || income.length}</strong><span>Total records</span></div>
      </header>
      <section className="rmn-panel" aria-label="Notification history">
        <div className="rmn-tools">
          <div className="rmn-tabs">
            <button type="button" className={`rmn-tab ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All</button>
            <button type="button" className={`rmn-tab ${filter === "unread" ? "active" : ""}`} onClick={() => setFilter("unread")}>Unread ({unreadCount})</button>
            <button type="button" className={`rmn-tab ${filter === "read" ? "active" : ""}`} onClick={() => setFilter("read")}>Read</button>
          </div>
          <div className="rmn-actions"><input className="rmn-search" aria-label="Search notifications on current page" placeholder="Search this page..." value={search} onChange={e => setSearch(e.target.value)} /><button type="button" className="rmn-btn" disabled={loading} onClick={() => fetchIncome(currentPage)}><i className="fas fa-sync-alt" /> Refresh</button></div>
        </div>
        {error && <div className="rmn-error" role="alert">{error}</div>}
        <div className="rmn-list">
          {filtered.length ? filtered.map((row, index) => <button type="button" key={row.id ?? index} className={`rmn-item ${isUnseen(row.status) ? "unread" : ""}`} onClick={() => openDetails(row.id)}>
            <div className="rmn-item-icon"><i className="fas fa-envelope" /></div>
            <div className="rmn-item-body"><p className="rmn-item-title">{row.title || "Notification"}</p><div className="rmn-date"><i className="far fa-clock" /> {row.date || "Date unavailable"}</div></div>
            <span className={`rmn-status ${isUnseen(row.status) ? "new" : ""}`}>{isUnseen(row.status) ? "New" : "Seen"}</span>
          </button>) : <div className="rmn-empty"><i className="far fa-bell-slash" />{loading ? "Fetching notifications..." : "No notifications found on this page."}</div>}
        </div>
        <footer className="rmn-footer"><span className="rmn-footnote">Page {currentPage} of {totalPages}{loading ? " · Updating..." : ""}</span>
          {totalPages > 1 && <nav className="rmn-pages" aria-label="Notification pages">
            <button type="button" className="rmn-page" disabled={currentPage <= 1 || loading} onClick={() => fetchIncome(currentPage - 1)} aria-label="Previous page">‹</button>
            {pages.map(page => <button type="button" key={page} className={`rmn-page ${page === currentPage ? "active" : ""}`} aria-current={page === currentPage ? "page" : undefined} disabled={loading} onClick={() => fetchIncome(page)}>{page}</button>)}
            <button type="button" className="rmn-page" disabled={currentPage >= totalPages || loading} onClick={() => fetchIncome(currentPage + 1)} aria-label="Next page">›</button>
          </nav>}
        </footer>
      </section>
    </div></main>
    {showModal && <div className="rmn-overlay" onMouseDown={e => { if (e.target === e.currentTarget) closeModal(); }}>
      <section className="rmn-modal" role="dialog" aria-modal="true" aria-label="Notification details">
        <div className="rmn-modal-head"><h3><i className="fas fa-bell" /> Notification Details</h3><button className="rmn-close" type="button" aria-label="Close" onClick={closeModal}>×</button></div>
        <div className="rmn-modal-body">{detailsLoading ? <div className="rmn-empty">Opening notification...</div> : detailsError ? <div className="rmn-error">{detailsError}</div> : <><h2 className="rmn-modal-title">{selectedNotification?.title || "Notification"}</h2><div className="rmn-modal-date"><i className="far fa-clock" /> {selectedNotification?.date || "Date unavailable"}</div><NotificationContent html={selectedNotification?.details || "No notification details available."} /></>}</div>
      </section>
    </div>}
  </>;
};

export default BasicDatatable;
